import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, MapPin, Calendar, Clipboard } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import DestinationSection from './DestinationSection';
import TripDetailsSection from './TripDetailsSection';
import ExpensesSection from './ExpensesSection';
import AdditionalInfoSection from './AdditionalInfoSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { calculateDateDifference } from '../../../../../utils/calculations/date';

// Импорт начального состояния формы из родительского компонента
import { initialFormData } from '../../PerDiemCalculatorPage';

/**
 * Компонент основной формы расчета суточных
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  expenseBreakdown,
  setExpenseBreakdown,
  russianRegions,
  foreignCountries,
  employeeCategories,
  businessPurposes,
  documentTypes,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Дополнительная логика при изменении полей
      // Если выбрано "Не включать проживание" - сбрасываем стоимость проживания
      if (name === 'includeAccommodation' && !checked) {
        newState.accommodationCostPerDay = '0';
      }
      
      // Если выбрано "Не включать транспорт" - сбрасываем стоимость транспорта
      if (name === 'includeTransportation' && !checked) {
        newState.transportationCost = '0';
      }
      
      // Если выбрано "Не включать дополнительные расходы" - сбрасываем связанные поля
      if (name === 'includeAdditionalExpenses' && !checked) {
        newState.additionalExpensesAmount = '0';
        newState.additionalExpensesDescription = '';
      }
      
      // Если изменен регион и rateOverride === false, обновляем ставку на нормативную
      if (name === 'region' && !prev.rateOverride) {
        const selectedRegion = russianRegions.find(region => region.id === value);
        if (selectedRegion) {
          newState.customDailyRate = selectedRegion.dailyRate.toString();
        }
      }
      
      // Если изменена страна и rateOverride === false, обновляем ставку на нормативную
      if (name === 'country' && !prev.rateOverride) {
        const selectedCountry = foreignCountries.find(country => country.id === value);
        if (selectedCountry) {
          newState.customDailyRate = selectedCountry.dailyRate.toString();
        }
      }
      
      // Если изменено назначение (внутренние/международные), обновляем нормативную ставку
      if (name === 'destination') {
        if (value === 'domestic') {
          const selectedRegion = russianRegions.find(region => region.id === prev.region);
          if (selectedRegion && !prev.rateOverride) {
            newState.customDailyRate = selectedRegion.dailyRate.toString();
          }
        } else {
          const selectedCountry = foreignCountries.find(country => country.id === prev.country);
          if (selectedCountry && !prev.rateOverride) {
            newState.customDailyRate = selectedCountry.dailyRate.toString();
          } else if (!prev.rateOverride) {
            // Германия по умолчанию
            const defaultCountry = foreignCountries.find(country => country.id === 'germany');
            if (defaultCountry) {
              newState.customDailyRate = defaultCountry.dailyRate.toString();
              newState.country = 'germany';
            }
          }
        }
      }
      
      // Если изменена категория сотрудника, и rateOverride === false,
      // умножаем базовую ставку на коэффициент категории
      if (name === 'employeeCategory' && !prev.rateOverride) {
        const selectedCategory = employeeCategories.find(cat => cat.id === value);
        
        let baseRate = 0;
        if (prev.destination === 'domestic') {
          const selectedRegion = russianRegions.find(region => region.id === prev.region);
          baseRate = selectedRegion ? selectedRegion.dailyRate : 0;
        } else {
          const selectedCountry = foreignCountries.find(country => country.id === prev.country);
          baseRate = selectedCountry ? selectedCountry.dailyRate : 0;
        }
        
        if (selectedCategory && baseRate > 0) {
          newState.customDailyRate = Math.round(baseRate * selectedCategory.rateMultiplier).toString();
        }
      }
      
      // Включение/выключение ручной настройки ставки
      if (name === 'rateOverride' && !checked) {
        // Возвращаем нормативную ставку, если отключили ручную настройку
        let baseRate = 0;
        if (prev.destination === 'domestic') {
          const selectedRegion = russianRegions.find(region => region.id === prev.region);
          baseRate = selectedRegion ? selectedRegion.dailyRate : 0;
        } else {
          const selectedCountry = foreignCountries.find(country => country.id === prev.country);
          baseRate = selectedCountry ? selectedCountry.dailyRate : 0;
        }
        
        // Умножаем на коэффициент категории сотрудника
        const selectedCategory = employeeCategories.find(cat => cat.id === prev.employeeCategory);
        if (selectedCategory && baseRate > 0) {
          newState.customDailyRate = Math.round(baseRate * selectedCategory.rateMultiplier).toString();
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [russianRegions, foreignCountries, employeeCategories, errors, setErrors, setFormData]);

  // Обработчик изменения чекбоксов типов документов
  const handleDocumentTypesChange = useCallback((e) => {
    const { value, checked } = e.target;
    
    setFormData(prev => {
      const currentDocumentTypes = [...prev.documentTypes];
      
      if (checked) {
        // Добавляем тип, если его еще нет
        if (!currentDocumentTypes.includes(value)) {
          currentDocumentTypes.push(value);
        }
      } else {
        // Удаляем тип, если он есть
        const index = currentDocumentTypes.indexOf(value);
        if (index !== -1) {
          currentDocumentTypes.splice(index, 1);
        }
      }
      
      return {
        ...prev,
        documentTypes: currentDocumentTypes
      };
    });
  }, [setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      startDate, endDate, region, country, destination,
      customDailyRate, transportationCost, accommodationCostPerDay,
      includeTransportation, includeAccommodation, includeAdditionalExpenses,
      additionalExpensesAmount, additionalExpensesDescription, 
      includeDocuments, documentTypes
    } = formData;

    // Валидация дат
    if (!startDate) {
      newErrors.startDate = 'Выберите дату начала командировки';
    }
    
    if (!endDate) {
      newErrors.endDate = 'Выберите дату окончания командировки';
    }
    
    if (startDate && endDate) {
      try {
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          newErrors.startDate = 'Некорректный формат даты';
        } else if (end < start) {
          newErrors.endDate = 'Дата окончания не может быть раньше даты начала';
        }
      } catch (error) {
        newErrors.startDate = 'Некорректный формат даты';
      }
    }
    
    // Валидация регионов и стран
    if (destination === 'domestic' && !region) {
      newErrors.region = 'Выберите регион командировки';
    }
    
    if (destination === 'international' && (!country || country === 'none')) {
      newErrors.country = 'Выберите страну командировки';
    }
    
    // Валидация ставки суточных
    const dailyRate = parseFloatOrDefault(customDailyRate, 0);
    if (dailyRate <= 0) {
      newErrors.customDailyRate = 'Ставка суточных должна быть положительным числом';
    }
    
    // Валидация расходов на транспорт, если включены
    if (includeTransportation) {
      const transportCost = parseFloatOrDefault(transportationCost, 0);
      if (transportCost <= 0) {
        newErrors.transportationCost = 'Укажите стоимость транспорта';
      }
    }
    
    // Валидация расходов на проживание, если включены
    if (includeAccommodation) {
      const accommodationCost = parseFloatOrDefault(accommodationCostPerDay, 0);
      if (accommodationCost <= 0) {
        newErrors.accommodationCostPerDay = 'Укажите стоимость проживания за сутки';
      }
    }
    
    // Валидация дополнительных расходов, если включены
    if (includeAdditionalExpenses) {
      const additionalCost = parseFloatOrDefault(additionalExpensesAmount, 0);
      if (additionalCost <= 0) {
        newErrors.additionalExpensesAmount = 'Укажите сумму дополнительных расходов';
      }
      
      if (!additionalExpensesDescription.trim()) {
        newErrors.additionalExpensesDescription = 'Укажите описание дополнительных расходов';
      }
    }
    
    // Валидация подтверждающих документов
    if (includeDocuments && documentTypes.length === 0) {
      newErrors.documentTypes = 'Выберите хотя бы один тип документа';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);
  
  // Функция расчета суточных и других расходов
  const calculatePerDiem = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setExpenseBreakdown([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготовка входных данных ---
        const {
          startDate, endDate, destination, region, country, employeeCategory,
          customDailyRate, includeTransportation, transportationCost,
          includeAccommodation, accommodationCostPerDay, includeAdditionalExpenses,
          additionalExpensesAmount, additionalExpensesDescription, taxDeductible,
          advancePayment, includeDocuments, documentTypes, businessPurpose,
          transportType, accommodationType, rateOverride
        } = formData;

        // Расчет продолжительности поездки
        const tripStartDate = new Date(startDate);
        const tripEndDate = new Date(endDate);
        const dateDiff = calculateDateDifference(tripStartDate, tripEndDate);
        
        // Добавляем 1 день, т.к. в командировочных учитывается и день начала, и день окончания
        const tripDurationDays = dateDiff.days + 1;
        
        // Определение ставки суточных
        const dailyRate = parseFloatOrDefault(customDailyRate, 0);
        
        // Нормативы суточных и проживания в зависимости от места командировки
        let standardDailyRate = 0;
        let accommodationLimit = 0;
        
        if (destination === 'domestic') {
          const selectedRegion = russianRegions.find(r => r.id === region);
          if (selectedRegion) {
            standardDailyRate = selectedRegion.dailyRate;
            accommodationLimit = selectedRegion.accommodationLimit;
          }
        } else {
          const selectedCountry = foreignCountries.find(c => c.id === country);
          if (selectedCountry) {
            standardDailyRate = selectedCountry.dailyRate;
            accommodationLimit = selectedCountry.accommodationLimit;
          }
        }
        
        // Учет коэффициента категории сотрудника
        const selectedCategory = employeeCategories.find(cat => cat.id === employeeCategory);
        const categoryMultiplier = selectedCategory ? selectedCategory.rateMultiplier : 1.0;
        
        if (!rateOverride) {
          standardDailyRate = Math.round(standardDailyRate * categoryMultiplier);
        }
        
        // Расчет суточных
        const totalPerDiem = dailyRate * tripDurationDays;
        
        // Транспортные расходы
        const transportCost = includeTransportation ? parseFloatOrDefault(transportationCost, 0) : 0;
        
        // Расходы на проживание
        const accommodationCostPerDayValue = includeAccommodation ? parseFloatOrDefault(accommodationCostPerDay, 0) : 0;
        // Для проживания учитываем на 1 день меньше, если командировка больше 1 дня
        // (считаем, что в последний день сотрудник возвращается и не ночует)
        const accommodationDays = tripDurationDays > 1 ? tripDurationDays - 1 : 0;
        const totalAccommodationCost = accommodationCostPerDayValue * accommodationDays;
        
        // Дополнительные расходы
        const additionalCost = includeAdditionalExpenses ? parseFloatOrDefault(additionalExpensesAmount, 0) : 0;
        
        // Общая сумма расходов
        const totalExpenses = totalPerDiem + transportCost + totalAccommodationCost + additionalCost;
        
        // Расчет превышения нормативов (для целей налогообложения)
        const standardPerDiemTotal = standardDailyRate * tripDurationDays;
        const perDiemExcess = Math.max(0, totalPerDiem - standardPerDiemTotal);
        
        // Превышение по проживанию
        const standardAccommodationTotal = accommodationLimit * accommodationDays;
        const accommodationExcess = Math.max(0, totalAccommodationCost - standardAccommodationTotal);
        
        // Расчет НДФЛ с превышения (если включен учет налогов)
        const taxRate = 0.13; // ставка НДФЛ 13%
        const taxableAmount = taxDeductible ? (perDiemExcess + accommodationExcess) : 0;
        const incomeTax = taxableAmount * taxRate;
        
        // Расчет суммы к выплате / возмещению
        const advancePaymentValue = parseFloatOrDefault(advancePayment, 0);
        const netPayment = totalExpenses - incomeTax - advancePaymentValue;
        
        // Формируем детализацию расходов по дням
        const expenseDetails = [];
        
        for (let i = 0; i < tripDurationDays; i++) {
          const currentDate = new Date(tripStartDate);
          currentDate.setDate(tripStartDate.getDate() + i);
          
          // Суточные начисляются за каждый день
          const dailyExpense = {
            date: currentDate.toISOString().split('T')[0],
            perDiem: dailyRate,
            accommodation: i < accommodationDays ? accommodationCostPerDayValue : 0,
            transport: i === 0 ? transportCost : 0, // Транспорт учитываем только в первый день для упрощения
            additional: i === 0 ? additionalCost : 0, // Дополнительные расходы тоже в первый день
            total: dailyRate + (i < accommodationDays ? accommodationCostPerDayValue : 0) + 
                   (i === 0 ? transportCost + additionalCost : 0)
          };
          
          expenseDetails.push(dailyExpense);
        }

        // Формируем результаты расчета
        const calculationResults = {
          tripStartDate,
          tripEndDate,
          tripDurationDays,
          dailyRate,
          standardDailyRate,
          totalPerDiem,
          transportCost,
          accommodationCostPerDay: accommodationCostPerDayValue,
          accommodationDays,
          totalAccommodationCost,
          additionalCost,
          additionalDescription: includeAdditionalExpenses ? additionalExpensesDescription : '',
          totalExpenses,
          standardPerDiemTotal,
          perDiemExcess,
          accommodationLimit,
          standardAccommodationTotal,
          accommodationExcess,
          taxableAmount,
          incomeTax,
          advancePayment: advancePaymentValue,
          netPayment,
          destinationType: destination,
          destinationName: destination === 'domestic' 
            ? russianRegions.find(r => r.id === region)?.name || '' 
            : foreignCountries.find(c => c.id === country)?.name || '',
          employeeCategoryName: selectedCategory?.name || '',
          hasDocuments: includeDocuments,
          documentsList: includeDocuments 
            ? documentTypes.map(dt => documentTypes.find(d => d.id === dt)?.name || dt).join(', ') 
            : '',
          businessPurposeName: businessPurposes.find(bp => bp.id === businessPurpose)?.name || '',
          transportTypeName: transportType,
          accommodationTypeName: accommodationType
        };

        setResults(calculationResults);
        setExpenseBreakdown(expenseDetails);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setExpenseBreakdown([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, russianRegions, foreignCountries, employeeCategories, businessPurposes, documentTypes, setErrors, setResults, setExpenseBreakdown, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData(initialFormData);
    setResults(null);
    setExpenseBreakdown([]);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт расчета в CSV
  const exportToCSV = () => {
    if (!results || !expenseBreakdown || expenseBreakdown.length === 0) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    // Формируем заголовки таблицы с расходами
    const headers = ['Дата', 'Суточные', 'Проживание', 'Транспорт', 'Дополнительно', 'Всего за день'];

    // Формируем строки с данными по дням
    const rows = expenseBreakdown.map(item => {
      const dateParts = item.date.split('-');
      const formattedDate = `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}`;

      return [
        formattedDate,
        formatCsvNumber(item.perDiem),
        formatCsvNumber(item.accommodation),
        formatCsvNumber(item.transport),
        formatCsvNumber(item.additional),
        formatCsvNumber(item.total)
      ].join(';');
    });

    // Добавляем итоговую строку
    const totalRow = [
      'ИТОГО:',
      formatCsvNumber(results.totalPerDiem),
      formatCsvNumber(results.totalAccommodationCost),
      formatCsvNumber(results.transportCost),
      formatCsvNumber(results.additionalCost),
      formatCsvNumber(results.totalExpenses)
    ].join(';');

    // Добавляем основную информацию в начало файла
    const startDate = new Date(results.tripStartDate).toLocaleDateString('ru-RU');
    const endDate = new Date(results.tripEndDate).toLocaleDateString('ru-RU');
    
    const infoRows = [
      ['Командировка:', results.destinationType === 'domestic' ? 'По России' : 'Зарубежная'],
      ['Место назначения:', results.destinationName],
      ['Период:', `с ${startDate} по ${endDate} (${results.tripDurationDays} дн.)`],
      ['Ставка суточных:', `${formatCsvNumber(results.dailyRate)} руб.`],
      ['Норматив суточных:', `${formatCsvNumber(results.standardDailyRate)} руб.`],
      ['Итого расходов:', `${formatCsvNumber(results.totalExpenses)} руб.`],
      ['К выплате:', `${formatCsvNumber(results.netPayment)} руб.`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_командировочных_расходов.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculatePerDiem();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры командировки
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Место назначения */}
        <DestinationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          russianRegions={russianRegions}
          foreignCountries={foreignCountries}
        />

        {/* Детали командировки */}
        <TripDetailsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          employeeCategories={employeeCategories}
          businessPurposes={businessPurposes}
        />

        {/* Расходы */}
        <ExpensesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительная информация */}
        <AdditionalInfoSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          handleDocumentTypesChange={handleDocumentTypesChange}
          errors={errors}
          documentTypes={documentTypes}
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать командировку'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {expenseBreakdown.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать отчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;